import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, Upload, CheckCircle, Loader, DollarSign, Building } from 'lucide-react';
import api from '../../api/apiClient';

const PROPERTY_TYPES = ['HOUSE', 'APARTMENT', 'TOWNHOUSE', 'UNIT', 'STUDIO', 'OTHER'];
const RENT_FREQUENCIES = ['WEEKLY', 'FORTNIGHTLY', 'MONTHLY'];
const STATES = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];

export default function OnboardProperty() {
  const navigate = useNavigate();

  // Basic details
  const [address, setAddress] = useState('');
  const [suburb, setSuburb] = useState('');
  const [state, setState] = useState('');
  const [postcode, setPostcode] = useState('');
  const [propertyType, setPropertyType] = useState('HOUSE');

  // Property specs
  const [bedrooms, setBedrooms] = useState('');
  const [bathrooms, setBathrooms] = useState('');
  const [carSpaces, setCarSpaces] = useState('');
  const [description, setDescription] = useState('');

  // Rent details
  const [rentAmount, setRentAmount] = useState('');
  const [rentFrequency, setRentFrequency] = useState('MONTHLY');
  const [bondAmount, setBondAmount] = useState('');

  // Payment routing
  const [bankName, setBankName] = useState('');
  const [bankBsb, setBankBsb] = useState('');
  const [bankAccount, setBankAccount] = useState('');
  const [bankAccountName, setBankAccountName] = useState('');

  const [bpayBillerCode, setBpayBillerCode] = useState('');
  const [bpayReference, setBpayReference] = useState('');

  // Bulk import
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importSource, setImportSource] = useState('CSV');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [propertyId, setPropertyId] = useState<number | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!address || !suburb || !state || !postcode) {
      setError('Please fill in all required address fields');
      return;
    }

    if (!rentAmount || parseFloat(rentAmount) <= 0) {
      setError('Please enter a valid rent amount');
      return;
    }

    if (!bankAccount && !bpayBillerCode) {
      setError('Please provide at least one payment method (Bank or BPAY)');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const payload: any = {
        address,
        suburb,
        state,
        postcode,
        propertyType,
        rentAmount: parseFloat(rentAmount),
        rentFrequency
      };

      if (bedrooms) payload.bedrooms = parseInt(bedrooms);
      if (bathrooms) payload.bathrooms = parseInt(bathrooms);
      if (carSpaces) payload.carSpaces = parseInt(carSpaces);
      if (description) payload.description = description;
      if (bondAmount) payload.bondAmount = parseFloat(bondAmount);

      if (bankAccount) {
        payload.bankDetails = {
          bankName,
          bsb: bankBsb,
          accountNumber: bankAccount,
          accountName: bankAccountName
        };
      }

      if (bpayBillerCode) {
        payload.bpayDetails = {
          billerCode: bpayBillerCode,
          reference: bpayReference
        };
      }

      const response = await api.post<{ propertyId: number }>('/owner/properties/onboard', payload);

      setPropertyId(response.propertyId);
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Failed to onboard property');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBulkImport = async () => {
    if (!importFile) {
      setError('Please select a file to import');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('source', importSource);

      const baseURL = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8787/api';
      const response = await fetch(`${baseURL}/owner/properties/import`, {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('rentry_access')}`
        }
      });

      const result = await response.json();

      if (result.status === 'success') {
        alert(`Import completed! ${result.successful} properties imported successfully, ${result.failed} failed.`);
        setShowBulkImport(false);
        navigate('/properties');
      } else {
        setError(result.message || 'Import failed');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to import properties');
    } finally {
      setSubmitting(false);
    }
  };

  if (success && propertyId) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4" style={{ backgroundColor: '#F8F8F8' }}>
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <CheckCircle className="w-16 h-16 mx-auto mb-4" style={{ color: '#49c5b6' }} />
          <h2 className="text-2xl font-bold mb-2" style={{ color: '#0C4432' }}>Property Onboarded!</h2>
          <p className="mb-4" style={{ color: '#666' }}>
            Your property has been successfully added to the system.
          </p>
          <div className="bg-gray-50 rounded-md p-4 mb-6">
            <p className="text-sm" style={{ color: '#666' }}>Property ID</p>
            <p className="text-2xl font-bold" style={{ color: '#0C4432' }}>#{propertyId}</p>
          </div>
          <div className="space-y-2">
            <button
              onClick={() => navigate('/properties')}
              className="w-full py-2 px-4 rounded-md text-white font-medium"
              style={{ backgroundColor: '#0C4432' }}
            >
              View Properties
            </button>
            <button
              onClick={() => window.location.reload()}
              className="w-full py-2 px-4 rounded-md border font-medium"
              style={{ borderColor: '#49c5b6', color: '#49c5b6' }}
            >
              Add Another Property
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4" style={{ backgroundColor: '#F8F8F8' }}>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Home className="w-8 h-8" style={{ color: '#0C4432' }} />
            <div>
              <h1 className="text-3xl font-bold" style={{ color: '#0C4432' }}>Onboard Property</h1>
              <p style={{ color: '#666' }}>Add a new property to your portfolio</p>
            </div>
          </div>

          <button
            onClick={() => setShowBulkImport(!showBulkImport)}
            className="flex items-center gap-2 px-4 py-2 border rounded-md hover:bg-gray-50"
            style={{ borderColor: '#49c5b6', color: '#49c5b6' }}
          >
            <Upload className="w-4 h-4" />
            Bulk Import
          </button>
        </div>

        {error && (
          <div className="mb-6 p-4 rounded-md" style={{ backgroundColor: '#FEE', color: '#DF6C4F' }}>
            {error}
          </div>
        )}

        {showBulkImport ? (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4" style={{ color: '#0C4432' }}>Bulk Import Properties</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#222' }}>
                  Import Source
                </label>
                <select
                  value={importSource}
                  onChange={(e) => setImportSource(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="CSV">CSV File</option>
                  <option value="XML">XML File</option>
                  <option value="PROPERTYME">PropertyMe Export</option>
                  <option value="REX">Rex Software</option>
                  <option value="CONSOLE">Console Cloud</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#222' }}>
                  Select File
                </label>
                <input
                  type="file"
                  accept=".csv,.xml"
                  onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>

              <div className="p-4 rounded-md" style={{ backgroundColor: '#E8F5F3' }}>
                <p className="text-sm" style={{ color: '#0C4432' }}>
                  <strong>CSV Format:</strong> address, suburb, state, postcode, type, bedrooms, bathrooms, rent_amount, rent_frequency, bank_bsb, bank_account, bpay_biller, bpay_ref
                </p>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={handleBulkImport}
                  disabled={submitting || !importFile}
                  className="flex-1 py-2 px-4 rounded-md text-white font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{ backgroundColor: '#0C4432' }}
                >
                  {submitting ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      Import Properties
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowBulkImport(false)}
                  className="px-4 py-2 border rounded-md"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Property Address */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-2 mb-4">
                <Building className="w-5 h-5" style={{ color: '#0C4432' }} />
                <h2 className="text-xl font-semibold" style={{ color: '#0C4432' }}>Property Address</h2>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#222' }}>
                    Street Address *
                  </label>
                  <input
                    type="text"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="123 Main Street"
                    className="w-full px-3 py-2 border rounded-md"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#222' }}>
                      Suburb *
                    </label>
                    <input
                      type="text"
                      value={suburb}
                      onChange={(e) => setSuburb(e.target.value)}
                      placeholder="Sydney"
                      className="w-full px-3 py-2 border rounded-md"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#222' }}>
                      State *
                    </label>
                    <select
                      value={state}
                      onChange={(e) => setState(e.target.value)}
                      className="w-full px-3 py-2 border rounded-md"
                      required
                    >
                      <option value="">Select State</option>
                      {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#222' }}>
                    Postcode *
                  </label>
                  <input
                    type="text"
                    value={postcode}
                    onChange={(e) => setPostcode(e.target.value)}
                    placeholder="2000"
                    className="w-full px-3 py-2 border rounded-md"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Property Details */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4" style={{ color: '#0C4432' }}>Property Details</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#222' }}>
                    Property Type *
                  </label>
                  <select
                    value={propertyType}
                    onChange={(e) => setPropertyType(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    {PROPERTY_TYPES.map(type => <option key={type} value={type}>{type}</option>)}
                  </select>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#222' }}>
                      Bedrooms
                    </label>
                    <input
                      type="number"
                      value={bedrooms}
                      onChange={(e) => setBedrooms(e.target.value)}
                      min="0"
                      className="w-full px-3 py-2 border rounded-md"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#222' }}>
                      Bathrooms
                    </label>
                    <input
                      type="number"
                      value={bathrooms}
                      onChange={(e) => setBathrooms(e.target.value)}
                      min="0"
                      className="w-full px-3 py-2 border rounded-md"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#222' }}>
                      Car Spaces
                    </label>
                    <input
                      type="number"
                      value={carSpaces}
                      onChange={(e) => setCarSpaces(e.target.value)}
                      min="0"
                      className="w-full px-3 py-2 border rounded-md"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#222' }}>
                    Description
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Modern family home with spacious backyard..."
                    className="w-full px-3 py-2 border rounded-md"
                    rows={4}
                  />
                </div>
              </div>
            </div>

            {/* Rent Details */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-2 mb-4">
                <DollarSign className="w-5 h-5" style={{ color: '#0C4432' }} />
                <h2 className="text-xl font-semibold" style={{ color: '#0C4432' }}>Rent Details</h2>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#222' }}>
                      Rent Amount *
                    </label>
                    <input
                      type="number"
                      value={rentAmount}
                      onChange={(e) => setRentAmount(e.target.value)}
                      placeholder="2000"
                      step="0.01"
                      className="w-full px-3 py-2 border rounded-md"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#222' }}>
                      Frequency *
                    </label>
                    <select
                      value={rentFrequency}
                      onChange={(e) => setRentFrequency(e.target.value)}
                      className="w-full px-3 py-2 border rounded-md"
                    >
                      {RENT_FREQUENCIES.map(freq => <option key={freq} value={freq}>{freq}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#222' }}>
                    Bond Amount
                  </label>
                  <input
                    type="number"
                    value={bondAmount}
                    onChange={(e) => setBondAmount(e.target.value)}
                    placeholder="4000"
                    step="0.01"
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
              </div>
            </div>

            {/* Payment Routing */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4" style={{ color: '#0C4432' }}>Payment Routing</h2>

              <div className="space-y-6">
                <div>
                  <h3 className="font-medium mb-3" style={{ color: '#222' }}>Bank Details</h3>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: '#222' }}>
                          Bank Name
                        </label>
                        <input
                          type="text"
                          value={bankName}
                          onChange={(e) => setBankName(e.target.value)}
                          placeholder="Commonwealth Bank"
                          className="w-full px-3 py-2 border rounded-md"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: '#222' }}>
                          BSB
                        </label>
                        <input
                          type="text"
                          value={bankBsb}
                          onChange={(e) => setBankBsb(e.target.value)}
                          placeholder="123-456"
                          className="w-full px-3 py-2 border rounded-md"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: '#222' }}>
                          Account Number
                        </label>
                        <input
                          type="text"
                          value={bankAccount}
                          onChange={(e) => setBankAccount(e.target.value)}
                          placeholder="12345678"
                          className="w-full px-3 py-2 border rounded-md"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: '#222' }}>
                          Account Name
                        </label>
                        <input
                          type="text"
                          value={bankAccountName}
                          onChange={(e) => setBankAccountName(e.target.value)}
                          placeholder="Property Trust Account"
                          className="w-full px-3 py-2 border rounded-md"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-medium mb-3" style={{ color: '#222' }}>BPAY Details (Alternative)</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: '#222' }}>
                        Biller Code
                      </label>
                      <input
                        type="text"
                        value={bpayBillerCode}
                        onChange={(e) => setBpayBillerCode(e.target.value)}
                        placeholder="123456"
                        className="w-full px-3 py-2 border rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: '#222' }}>
                        Reference Number
                      </label>
                      <input
                        type="text"
                        value={bpayReference}
                        onChange={(e) => setBpayReference(e.target.value)}
                        placeholder="9876543210"
                        className="w-full px-3 py-2 border rounded-md"
                      />
                    </div>
                  </div>
                </div>

                <div className="p-3 rounded-md" style={{ backgroundColor: '#FFF3CD' }}>
                  <p className="text-sm" style={{ color: '#856404' }}>
                    At least one payment method (Bank or BPAY) is required
                  </p>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 px-4 rounded-md text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ backgroundColor: '#0C4432' }}
            >
              {submitting ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Onboarding Property...
                </>
              ) : (
                <>
                  <Home className="w-5 h-5" />
                  Onboard Property
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

